import { useMemo, useState } from 'react';
import { Alert, Button, Form as AntForm, Grid, Input, Typography } from 'antd';
import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, KeyRound } from 'lucide-react';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { authApi } from '@api/auth';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import type { Translator } from '@/i18n/config';
import { ROUTES } from '@shared/constants/routes';
import { recordAuditAction } from '@features/audit/recordAction';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const PASSWORD_RULE = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;

function makeSchema(t: Translator) {
  return z
    .object({
      password: z
        .string()
        .min(8, t('reset.validation.passwordMin'))
        .regex(PASSWORD_RULE, t('reset.validation.passwordPattern')),
      confirm: z.string().min(1, t('validation:passwordConfirmRequired')),
    })
    .refine((data) => data.password === data.confirm, {
      message: t('validation:passwordMismatch'),
      path: ['confirm'],
    });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

function strength(pw: string): { score: number; color: string; key: 'weak' | 'medium' | 'strong' } {
  let score = 0;
  if (pw.length >= 8) score += 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  if (pw.length >= 12) score += 1;
  if (score <= 2) return { score, color: 'var(--ds-danger)', key: 'weak' };
  if (score === 3) return { score, color: 'var(--ds-warning)', key: 'medium' };
  return { score, color: 'var(--ds-success)', key: 'strong' };
}

export function ResetPasswordPage() {
  const { t } = useTranslation(['auth', 'common', 'validation', 'errors']);
  const screens = useBreakpoint();
  const isWide = screens.lg;
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [serverError, setServerError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [pw, setPw] = useState('');
  const schema = useMemo(() => makeSchema(t as unknown as Translator), [t]);

  const tokenValid = useMemo(() => token.trim().length > 0, [token]);

  const mutation = useMutation({
    mutationFn: (values: Values) => authApi.resetPassword(token, values.password),
    onSuccess: () => {
      setDone(true);
      void recordAuditAction({
        action: 'auth.password.reset',
        resource: 'auth',
        summary: 'Şifre sıfırlama bağlantısı kullanılarak şifre değiştirildi',
      });
    },
    onError: (err) => {
      setServerError(resolveApiErrorMessage(err));
    },
  });

  const meter = strength(pw);

  return (
    <div className="ds-login-shell">
      {isWide ? (
        <aside className="ds-login-brand" aria-hidden="true">
          <div className="ds-login-brand__inner">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div className="ds-login-brand__mark"><img src="/logo.svg" alt="" /></div>
              <div style={{ lineHeight: 1.15 }}>
                <div style={{ fontWeight: 700, fontSize: 16 }}>{t('brand.title')}</div>
                <div style={{ fontSize: 12, opacity: 0.8 }}>{t('brand.subtitle')}</div>
              </div>
            </div>
          </div>
          <div className="ds-login-brand__inner" style={{ maxWidth: 420 }}>
            <h2 className="ds-login-brand__heading">{t('reset.panel.heading')}</h2>
            <p className="ds-login-brand__lead">
              {t('reset.panel.lead')}
            </p>
          </div>
        </aside>
      ) : null}

      <section className="ds-login-form-section">
        <div className="ds-login-form">
          {!isWide ? (
            <div className="ds-auth-card__brand">
              <div className="ds-auth-card__brand-mark"><img src="/logo.svg" alt="" /></div>
              <div className="ds-auth-card__brand-text">
                <div className="ds-auth-card__brand-name">{t('brand.title')}</div>
                <div className="ds-auth-card__brand-sub">{t('brand.cardSubtitle')}</div>
              </div>
            </div>
          ) : null}

          {!tokenValid ? (
            <>
              <Title level={3} className="ds-auth-card__title">
                {t('reset.invalid.title')}
              </Title>
              <Text className="ds-auth-card__subtitle">
                {t('reset.invalid.description')}
              </Text>
              <Button
                type="primary"
                block
                size="large"
                style={{ marginTop: 16 }}
                onClick={() => void navigate(ROUTES.FORGOT_PASSWORD)}
              >
                {t('reset.invalid.requestNew')}
              </Button>
            </>
          ) : done ? (
            <>
              <div
                style={{
                  display: 'inline-flex',
                  width: 48,
                  height: 48,
                  borderRadius: 12,
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: 'rgba(34,197,94,0.12)',
                  color: 'var(--ds-success)',
                  marginBottom: 12,
                }}
              >
                <CheckCircle2 size={24} />
              </div>
              <Title level={3} className="ds-auth-card__title">
                {t('reset.done.title')}
              </Title>
              <Text className="ds-auth-card__subtitle">
                {t('reset.done.description')}
              </Text>
              <Button
                type="primary"
                block
                size="large"
                style={{ marginTop: 16 }}
                onClick={() => void navigate(ROUTES.LOGIN, { replace: true })}
              >
                {t('reset.done.goToLogin')}
              </Button>
            </>
          ) : (
            <>
              <Title level={3} className="ds-auth-card__title">
                {t('reset.title')}
              </Title>
              <Text className="ds-auth-card__subtitle">
                {t('reset.subtitle')}
              </Text>

              {serverError ? (
                <Alert
                  type="error"
                  message={serverError}
                  showIcon
                  closable
                  onClose={() => setServerError(null)}
                  style={{ marginBottom: 16 }}
                />
              ) : null}

              <Form
                schema={schema}
                defaultValues={{ password: '', confirm: '' }}
                onSubmit={(values) => mutation.mutate(values)}
              >
                <AntForm layout="vertical" component="div">
                  <FormField name="password" label={t('reset.fields.passwordLabel')} required>
                    <Input.Password
                      autoComplete="new-password"
                      placeholder={t('reset.fields.passwordPlaceholder')}
                      size="large"
                      prefix={<KeyRound size={14} />}
                      onChange={(e) => setPw(e.target.value)}
                    />
                  </FormField>
                  {pw ? (
                    <div style={{ marginTop: -8, marginBottom: 12 }}>
                      <div
                        style={{
                          height: 4,
                          borderRadius: 2,
                          background: 'var(--ds-border-subtle)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${(meter.score / 5) * 100}%`,
                            background: meter.color,
                            transition: 'width 160ms ease',
                          }}
                        />
                      </div>
                      <div style={{ fontSize: 12, color: meter.color, marginTop: 4 }}>
                        {t('reset.strength.label', { level: t(`reset.strength.${meter.key}`) })}
                      </div>
                    </div>
                  ) : null}
                  <FormField name="confirm" label={t('reset.fields.confirmLabel')} required>
                    <Input.Password
                      autoComplete="new-password"
                      placeholder={t('reset.fields.confirmPlaceholder')}
                      size="large"
                    />
                  </FormField>
                </AntForm>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={mutation.isPending}
                >
                  {t('reset.submit')}
                </Button>
              </Form>

              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Link to={ROUTES.LOGIN}>{t('reset.backToLogin')}</Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
